<template>
  <div class="Banner">
    <div class="Banner-inner PageContainer">
      <Tag
        v-if="tag"
        class="Banner-tag"
        :type="tagType"
        :variant="tagStyle"
        :leftIcon="tagLeftIcon"
        :rightIcon="tagRightIcon"
        size="lg"
      >
        {{ tag }}
      </Tag>

      <div class="Banner-text">
        <p class="Banner-title">
          {{ title }}
        </p>

        <p
          v-if="subtitle"
          class="Banner-subtitle"
        >
          {{ subtitle }}
        </p>
      </div>

      <Countdown
        v-if="countdownEnd"
        class="Banner-countdown"
        :end="countdownEnd"
        :format="countdownFormat"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IconName } from '../../atoms/Icon/Icon.model';
import type { TagType, TagStyle } from '../../atoms/Tag/Tag.model';
import Tag from '../../atoms/Tag/Tag.vue';
import Countdown from '../../molecules/Countdown/Countdown.vue';

interface BannerProps {
  title: string;
  subtitle?: string;
  tag?: string;
  tagType?: TagType;
  tagStyle?: TagStyle;
  tagLeftIcon?: IconName;
  tagRightIcon?: IconName;
  countdownEnd?: Date;
  countdownFormat?: string;
}

withDefaults(defineProps<BannerProps>(), {
  countdownFormat: 'HH:MM:SS',
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Banner {
    background-color: var(--banner-bg-color, #{vars.$atoms-banner-bg-color-default});

    &-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: vars.$dimensions-d-8;
      padding-block: vars.$dimensions-d-3;
      padding-inline: vars.$dimensions-d-8;
      color: var(--banner-text-color, #{vars.$atoms-banner-text-color});

      @include responsive.mobile {
        flex-direction: column;
        align-items: center;
        gap: vars.$dimensions-d-5;
        padding: vars.$dimensions-d-3 vars.$dimensions-d-5;
      }
    }

    &-tag {
      flex-shrink: 0;
    }

    &-text {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-1;
      flex: 1;

      @include responsive.mobile {
        text-align: center;
      }
    }

    &-title {
      @include text.heading(h5);
      color: inherit;
      margin: 0;
    }

    &-subtitle {
      font-size: vars.$font-size-md;
      margin: 0;
      color: inherit;
    }

    &-countdown {
      flex-shrink: 0;
      color: inherit;
    }
  }
}
</style>
